import { useCallback, useRef, useState } from "react";
import type { NaraBlock } from "./types";
import { naraSeed } from "./fixtures";

/**
 * Simulated Nara streaming. Real wiring would stream tokens from ai-agent;
 * here we reveal a scripted reply token-by-token and then "generate" blocks
 * one at a time, so Make feels like a live conversation with a colleague.
 */
export interface UseNaraResult {
  streaming: boolean;
  reply: string;
  blocks: NaraBlock[];
  send: (prompt: string) => void;
  reset: () => void;
}

function scriptFor(prompt: string): string {
  const p = prompt.toLowerCase();
  if (p.includes("pricing"))
    return "On it. I'll rebuild the pricing as stacked, thumb-friendly cards for mobile, keep the three tiers, and move the coral 'Most popular' badge up. Drafting the blocks now —";
  if (p.includes("product hunt") || p.includes("hunt"))
    return "Love it. A Product Hunt launch page with a 'Hello, hunters' hero, your launch-day offer, and PH social proof. Spinning up the blocks —";
  if (p.includes("waitlist"))
    return "Got it. A focused waitlist page — one field, social proof, and a calm coral CTA. Here's the structure —";
  if (p.includes("a/b") || p.includes("test"))
    return "Good call. I'll set up an A/B test, draft variant B, and split traffic 50/50. Results will flow straight back into Signals. Drafting —";
  return "Happy to. I'll draft a warm, on-brand landing page that leads with the outcome and keeps coral scarce. Building the blocks —";
}

export function useNara(): UseNaraResult {
  const [streaming, setStreaming] = useState(false);
  const [reply, setReply] = useState("");
  const [blocks, setBlocks] = useState<NaraBlock[]>([]);
  const timers = useRef<number[]>([]);

  const clear = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  const reset = useCallback(() => {
    clear();
    setStreaming(false);
    setReply("");
    setBlocks([]);
  }, []);

  const send = useCallback((prompt: string) => {
    clear();
    setStreaming(true);
    setReply("");
    setBlocks([]);
    const script = scriptFor(prompt);
    const words = script.split(" ");
    words.forEach((w, i) => {
      const t = window.setTimeout(() => {
        setReply((r) => (r ? r + " " + w : w));
      }, 40 + i * 38);
      timers.current.push(t);
    });
    const afterText = 40 + words.length * 38 + 200;
    naraSeed.forEach((block, i) => {
      const t = window.setTimeout(() => {
        setBlocks((b) => [...b, block]);
        if (i === naraSeed.length - 1) setStreaming(false);
      }, afterText + i * 520);
      timers.current.push(t);
    });
  }, []);

  return { streaming, reply, blocks, send, reset };
}
